import { store } from "@/store/store"
import { atom } from "jotai"
import { getEditor } from "@/utils/useEditor"
import { AdjustmentsHistoryRecord, CatalogManager, PhotoRecord } from "@/lib/catalog"
import { HistoryRecordAction } from "@/lib/catalog/types"
import equal from "fast-deep-equal"
import {
	currentPhotoAtom,
	currentPhotoIdAtom,
	selectedPhotosAtom,
	updatePhoto,
} from "@/store/photo/photos-state"
import { AppState, stateAtom, WbOption } from "@/store/undoable-state"
import {
	AdjustmentsResult,
	queuePasteAdjustments,
	queueWaitBlock,
	resolveWaitBlock,
} from "./processing/image-processing"
import { storeAdjustmentsHistoryAndUpdatePhoto } from "./processing/processing-storage"
import { clamp } from "@/core/utils/math"
import {
	AdjustmentsOptions,
	getUpdatedHistoryWithAdjustments,
	getUpdatedPhotoRecord,
} from "@/lib/catalog/utils"
import { isCalibrationAtom, projectAtom } from "@/store/project/projects-state"
import {
	ensureCullingClusterLeaderNotInvalid,
	ensurePhotoNotKeyNeighbor,
} from "@/lib/ai/ai-cluster"
import { resetBiasesInImageInfo } from "./processing/image-info"
import { AdjustmentGroups, defaultMaskAdjustments, getDefaultAdjustments } from "@/core/adjustments"
import { AdjustmentKey, Adjustments, MaskGroupAdjustments } from "@/core/types"
import { showToast } from "./ui-views-state"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer toast i18n
import i18n from "@/i18n"
// eslint-disable-next-line no-restricted-imports
import { getHistoryChangedValueDisplay, translateHistoryMessage } from "@/i18n/translate-history"
import { replaceDialog } from "./dialogs-state"
import { CopyAdjustmentsDialog } from "@/components/Dialogs/SelectAdjustmentsDialog"
import { transformLocalAdjustments } from "@/utils/math"
import { isDeepEqual } from "@/utils/object"
import { analytics } from "@/telemetry/amplitude"
import toast from "react-hot-toast"
import { photoLoadingStateAtom } from "./editor/photo-loading"
import { lockedSinceSetup } from "./app-state"
import { isRetouchingMasksDebugEnabled } from "@/utils/measurement/feature-flag"

export type AdjustmentHistoryTrackingProperties = Record<string, unknown>

const clearHealingAiCandidateOnReset = async () => {
	const { cancelHealingAiGeneration, clearAiHealingCandidate } =
		await import("./editor/healing-state")
	cancelHealingAiGeneration()
	clearAiHealingCandidate()
}

export type Action = {
	type: "update" | "undo" | "redo"
	targets?: string[]
	value?: AppState
	name?: string
}

export const baseHistoryAtom = atom({
	index: 0,
	history: [] as AppState[],
	displayNames: [] as string[],
})

export const historyAtom = atom(
	(get) => {
		const { history, index, displayNames } = get(baseHistoryAtom)

		const state = history[index] ?? get(stateAtom)

		return {
			index,
			state,
			name: displayNames[index],
			canRedo: index < history.length - 1,
			canUndo: index > 0,
		}
	},
	(get, set, action: Action) => {
		let { history, index, displayNames } = get(baseHistoryAtom)

		const prevIndex = index

		if (action.type === "update") {
			const name = action.name ?? "Update"
			const currentState = action.value ?? get(stateAtom)

			index = index + 1
			history = history.slice(0, index + 1).concat(currentState)
			displayNames = displayNames.slice(0, index + 1).concat(name)
		} else if (action.type === "undo") {
			index = Math.max(index - 1, 0)
		} else if (action.type === "redo") {
			index = Math.min(index + 1, history.length - 1)
		}

		set(baseHistoryAtom, {
			index,
			history,
			displayNames,
		})

		const prevState = history[prevIndex]
		const currState = history[index]

		if (prevState && currState) {
			persistStateChanges(prevState, currState)
		}
	},
)

export const adjustmentsHistoryAtom = atom<AdjustmentsHistoryRecord[]>([])

export const undoableAdjustmentsHistoryAtom = atom((get) => {
	const currentPhoto = get(currentPhotoAtom)
	const loadingState = get(photoLoadingStateAtom)
	const state = get(adjustmentsHistoryAtom)

	if (!currentPhoto || loadingState !== "loaded") {
		return {
			state,
			index: 0,
			canUndo: false,
			canRedo: false,
		}
	}

	const index = currentPhoto.historyIndex ?? 0

	return {
		state,
		index,
		canRedo: index < state.length - 1,
		canUndo: index > 0,
	}
})

const persistStateChanges = (prevState: AppState, currState: AppState) => {
	// Persist changes in photos
	for (const key in currState.photos) {
		const currPhoto = currState.photos[key]
		const prevPhoto = prevState.photos[key]

		if (currPhoto && prevPhoto !== currPhoto) {
			CatalogManager.updatePhotoRecord(currPhoto.id, currPhoto)

			// TODO: render and update preview if historyIndex changes

			if (import.meta.env.DEV) {
				console.log("Storing photo record", prevPhoto, currPhoto)
			}
		}
	}
}

export function setHistoryIndex(value: number | "undo" | "redo", target?: EventTarget) {
	const isIndex = typeof value === "number"

	const currentPhoto = store.get(currentPhotoAtom)
	const history = store.get(undoableAdjustmentsHistoryAtom)

	if (!currentPhoto) {
		return
	}

	if (value === "undo" && !history.canUndo) {
		return
	}

	if (value === "redo" && !history.canRedo) {
		return
	}

	const index = currentPhoto.historyIndex ?? 0
	const nextIndex = isIndex ? value : value === "undo" ? index - 1 : index + 1

	const record = history.state[index]
	const nextRecord = history.state[nextIndex]

	if (!record || !nextRecord) {
		return
	}

	updatePhoto(currentPhoto.id, { historyIndex: nextIndex })
	setHistoryRecord(history.state, nextRecord)

	if (!isIndex) {
		const tComponents = (key: string, params?: Record<string, unknown>) =>
			i18n.t(`components:${key}`, params)
		const displayMessage = translateHistoryMessage(record, tComponents)
		const changedValue = getHistoryChangedValueDisplay(record, tComponents)
		const suffix = changedValue ? " " + changedValue : ""
		toast(
			value === "redo"
				? i18n.t("store:storeHistory.redoToast", { action: displayMessage + suffix })
				: i18n.t("store:storeHistory.undoToast", { action: displayMessage + suffix }),
		)
	}

	analytics.trackEvent(
		{
			label: "history_item",
			type: "click",
			value: HistoryRecordAction[nextRecord.action],
		},
		target,
	)
}

export const currentWhiteBalanceSettingAtom = atom<WbOption>("as-shot")

export const currentAdjustmentsAtom = atom((get) => {
	const history = get(adjustmentsHistoryAtom)
	const currentPhoto = get(currentPhotoAtom)

	return getCurrentAdjustmentsFromHistory(currentPhoto, history)
})

export const activeAdjustmentsAtom = atom((get) => {
	const editor = getEditor()
	const currentAdjustments = get(currentAdjustmentsAtom)
	const currentPhoto = get(currentPhotoAtom)
	const defaultAdjustments = getDefaultAdjustments(currentPhoto)

	const compareMainAdjustment = (key: string) =>
		!isDeepEqual(currentAdjustments[key], defaultAdjustments[key])
	const compareMaskAdjustment = (key: string, adjustments: MaskGroupAdjustments) =>
		adjustments[key] !== defaultMaskAdjustments[key]

	/**
	 * Compare global adjustments
	 */

	const basic = AdjustmentGroups.basic.some((key) => {
		// Special case for temp/tint
		if (key === "temperature") {
			const defaultTemp = Math.round(editor.photo.whiteBalance.asShotTemperature)
			const value = currentAdjustments[key] ?? defaultTemp

			return value !== defaultTemp
		}

		if (key === "tint") {
			const defaultTint = Math.round(editor.photo.whiteBalance.asShotTint)
			const value = currentAdjustments[key] ?? defaultTint

			return value !== defaultTint
		}

		return currentAdjustments[key] !== defaultAdjustments[key]
	})

	const pointCurves = AdjustmentGroups.basicCurves.some(compareMainAdjustment)
	const parametricCurves = AdjustmentGroups.parametricCurves.some(compareMainAdjustment)
	const hsl = AdjustmentGroups.HSL.some(compareMainAdjustment)
	const colorGrading = AdjustmentGroups.colorGrading.some(compareMainAdjustment)
	const detail = AdjustmentGroups.detail.some(compareMainAdjustment)
	const transform = AdjustmentGroups.transform.some(compareMainAdjustment)
	const perspectiveCorrection = AdjustmentGroups.transform
		.filter((key) => key !== "lensDistortion")
		.some(compareMainAdjustment)
	const effects = AdjustmentGroups.effects.some(compareMainAdjustment)
	const calibration = AdjustmentGroups.calibration.some(compareMainAdjustment)
	const crop = AdjustmentGroups.cropGroup.some(compareMainAdjustment)

	const globalAdjustments =
		basic ||
		pointCurves ||
		parametricCurves ||
		hsl ||
		colorGrading ||
		detail ||
		transform ||
		effects ||
		calibration

	/**
	 * Compare mask adjustments
	 */

	const maskAdjustmentKeys = Object.keys(defaultMaskAdjustments)
	const maskGroups = new Set<string>()

	currentAdjustments.localAdjustments?.forEach((group) => {
		if (group.retouching && !isRetouchingMasksDebugEnabled()) {
			return
		}

		const isActive = maskAdjustmentKeys.some((key) => compareMaskAdjustment(key, group))

		if (isActive) {
			maskGroups.add(group.id)
		}
	})

	const masking = maskGroups.size > 0

	/**
	 * Compare healingMasks
	 */

	const healing = (currentAdjustments.healingMasks?.length ?? 0) > 0
	return {
		// Global adjustments
		crop,
		globalAdjustments,
		masking,
		healing,

		// Individual adjustment groups
		basic,
		pointCurves,
		parametricCurves,
		hsl,
		colorGrading,
		detail,
		transform,
		perspectiveCorrection,
		effects,
		calibration,

		// Individual mask groups
		maskGroups,
	}
})

export const getCurrentAdjustmentsWithKeys = (keys: Set<AdjustmentKey>) => {
	const editor = getEditor()
	const currentAdjustments = store.get(currentAdjustmentsAtom)

	const selectedAdjustments = {}

	for (const key of keys) {
		if (key === "temperature") {
			selectedAdjustments[key] =
				currentAdjustments.temperature ?? editor.whiteBalance.asShotTemperature
		} else if (key === "tint") {
			selectedAdjustments[key] = currentAdjustments.tint ?? editor.whiteBalance.asShotTint
		} else if (key === "localAdjustments") {
			// transform masks to on-screen cropped image coordinate system
			const localAdjustments = currentAdjustments["localAdjustments"]
			if (!localAdjustments) continue
			const editor = getEditor()
			selectedAdjustments[key] = transformLocalAdjustments(
				localAdjustments,
				currentAdjustments,
				editor.originalImageSize,
				editor.initialOrientation,
				true,
			)
		} else {
			selectedAdjustments[key] = currentAdjustments[key]
		}
	}
	return selectedAdjustments
}

export const hasAdjustmentsToCopyAtom = atom((get) => {
	const adjustments = get(currentAdjustmentsAtom)
	const currentPhoto = get(currentPhotoAtom)
	const defaultAdjustments = getDefaultAdjustments(currentPhoto)
	return !equal(adjustments, defaultAdjustments)
})

export const hasAdjustmentsToPasteAtom = atom((get) => {
	const adjustments = get(adjustmentsClipBoardAtom)
	const currentPhoto = get(currentPhotoAtom)
	const defaultAdjustments = getDefaultAdjustments(currentPhoto)
	return adjustments && !equal(adjustments, defaultAdjustments)
})

export const getCurrentAdjustmentsFromHistory = (
	photo: PhotoRecord | null,
	history?: AdjustmentsHistoryRecord[],
): Adjustments => {
	const adjustmentHistory = history ?? store.get(adjustmentsHistoryAtom)
	const defaultAdjustments = getDefaultAdjustments(photo)
	// Get adjustments for the photo history index, or the most recent one
	const index = photo?.historyIndex ?? adjustmentHistory.length - 1
	const record = adjustmentHistory[Math.min(index, adjustmentHistory.length - 1)]

	return record?.adjustments ?? defaultAdjustments
}

export const resetCurrentHistoryRecord = async () => {
	const currentPhoto = store.get(currentPhotoAtom)

	if (!currentPhoto) {
		return
	}

	await clearHealingAiCandidateOnReset()

	const history = store.get(adjustmentsHistoryAtom)
	const originalRecord = history[0]

	if (originalRecord) {
		const result: AdjustmentsResult = {
			adjustments: originalRecord.adjustments,
			preview: null,
			originalPreview: null,
			imageInfo: null,
		}

		await storeAdjustmentsHistoryAndUpdatePhoto(currentPhoto, result, {
			message: "Reset All", // i18n-ignore — internal history record identifier
			action: HistoryRecordAction.Reset,
		})

		saveCurrentAppState("Update adjustments")
	}
}

export const setHistoryRecord = (
	history: AdjustmentsHistoryRecord[],
	record: AdjustmentsHistoryRecord,
	photo?: PhotoRecord,
) => {
	const targetPhoto = photo ?? store.get(currentPhotoAtom)

	if (!targetPhoto) {
		return
	}
	const isCalibration = store.get(isCalibrationAtom)
	const prevKind = targetPhoto.kind
	const kind = isCalibration ? "default" : "locked"

	const newHistory = history.slice(0, record.stepIndex + 1)
	const updatedPhoto = getUpdatedPhotoRecord(newHistory, record.stepIndex)

	updatePhoto(targetPhoto.id, {
		...updatedPhoto,
		kind: kind,
	})

	if (prevKind != "locked" && kind == "locked") {
		ensurePhotoNotKeyNeighbor(targetPhoto.id)
		ensureCullingClusterLeaderNotInvalid(targetPhoto.id)
		store.set(lockedSinceSetup, store.get(lockedSinceSetup) + 1)
	}

	if (prevKind == "reference" && isCalibration) resetBiasesInImageInfo(targetPhoto.id)
	// Todo: trigger reprocesssing when a photo is automatically unmarked as reference

	updateWhiteBalanceSettingIfNeeded(record.adjustments)

	saveCurrentAppState(`Reverted to ${record.message} ${record.changedValue}}`)
	// storeCurrentPreview()
}

export const startSavingAdjustmentState = () => {
	queueWaitBlock("adjustments")
}

export const saveCurrentAdjustmentState = async (options: {
	message: string
	changedValue?: string
	trackingProperties?: AdjustmentHistoryTrackingProperties
}) => {
	const adjustments = getEditor().adjustments
	const currentPhoto = store.get(currentPhotoAtom)

	if (!currentPhoto) {
		resolveWaitBlock("adjustments")
		return
	}

	const result: AdjustmentsResult = {
		adjustments,
		preview: null,
		originalPreview: null,
		imageInfo: null,
	}

	try {
		await storeAdjustmentsHistoryAndUpdatePhoto(currentPhoto, result, {
			message: options.message,
			changedValue: options.changedValue,
			action: HistoryRecordAction.ManuallyAdjust,
			trackingProperties: options.trackingProperties,
		})

		saveCurrentAppState("Update adjustments")
	} finally {
		resolveWaitBlock("adjustments")
	}
}

export const saveCurrentAppState = (name: string) => {
	store.set(historyAtom, { type: "update", name: name })
}

export function updateCurrentAdjustmentHistoryIfNeeded(
	photo: PhotoRecord,
	adjustmentOptions: AdjustmentsOptions,
	trackingProperties?: AdjustmentHistoryTrackingProperties,
) {
	const currentPhotoId = store.get(currentPhotoIdAtom)

	if (currentPhotoId === photo.id) {
		const currentHistory = store.get(adjustmentsHistoryAtom)
		const result = getUpdatedHistoryWithAdjustments(photo, currentHistory, adjustmentOptions)

		if (typeof result.newHistory !== "undefined") {
			store.set(adjustmentsHistoryAtom, result.newHistory)
		}

		const newRecord = result.newHistory[result.newHistory.length - 1]
		if (newRecord) {
			const projectId = store.get(projectAtom)?.id
			analytics.trackEvent({
				label: "adjustment_history_updated",
				value: newRecord.message,
				type: "event",
				customProperties: {
					...(projectId ? { projectId } : {}),
					...(trackingProperties ?? {}),
				},
			})
		}

		updateWhiteBalanceSettingIfNeeded(result.record.adjustments)
	}
}

export async function fetchCurrentAdjustmentsFromHistory(photo: PhotoRecord) {
	const index = photo.historyIndex ?? 0
	const record = await CatalogManager.fetchAdjustmentHistoryRecord(photo.id, index)
	const defaultAdjustments = getDefaultAdjustments(photo)

	// Backward compatibility
	const currentAdjustments = record?.adjustments
		? { ...defaultAdjustments, ...record.adjustments }
		: defaultAdjustments

	currentAdjustments.localAdjustments = record?.adjustments.localAdjustments?.map(
		(maskGroup) => ({
			...defaultMaskAdjustments,
			...maskGroup,
		}),
	)
	return currentAdjustments
}

// TODO: We should be able to get current applied preset from PhotoRecord directly
export async function fetchPresetIDsFromHistory(photo: PhotoRecord) {
	const history = await CatalogManager.fetchAdjustmentHistoryRecords(photo, [
		"presetId",
		"action",
	])

	const index = clamp(photo.historyIndex ?? 0, 0, history.length - 1)

	// Image is at original state. No need to check for applied preset
	if (index === 0) {
		return []
	}

	const currentPresetIDs: string[] = []

	for (let i = index; i >= 0; i--) {
		const record = history[i]
		if (
			!record ||
			record.action === HistoryRecordAction.PasteAdjustments ||
			record.action === HistoryRecordAction.ApplyColorMatch ||
			record.action === HistoryRecordAction.Reset
		)
			break
		if (record.action === HistoryRecordAction.AutoEnhance) {
			if (record.presetId) continue
			else break
		}
		if (record.presetId) {
			currentPresetIDs.push(record.presetId)
		}
		if (record.action === HistoryRecordAction.ApplyAIStyle) break
	}

	return currentPresetIDs
}

// Determine if user made manual changes on top of latest AI Style effect
// This is correct as long as we don't allow user to manually clear history and don't support multiple styles
export async function hasManualChanges(photo: PhotoRecord) {
	const index = photo.historyIndex ?? 0
	const history = await CatalogManager.fetchAdjustmentHistoryRecords(photo, ["styleId", "action"])
	const record = history[index]

	if (!history || !record) {
		return true
	}

	return !(
		index === history.length - 1 &&
		record.styleId != null &&
		record.action == HistoryRecordAction.ApplyAIStyle
	)
}

export const adjustmentsClipBoardAtom = atom<Partial<Adjustments> | null>(null)

export function showCopySettings() {
	replaceDialog(CopyAdjustmentsDialog)
}

export function copyCurrentAdjustments(keys: Set<AdjustmentKey>) {
	const adjustmentsToCopy = getCurrentAdjustmentsWithKeys(keys)
	store.set(adjustmentsClipBoardAtom, adjustmentsToCopy)

	showToast(i18n.t("store:storeHistory.editsCopied"))
}

export function pasteAdjustments() {
	const adjustments = store.get(adjustmentsClipBoardAtom)
	const selectedPhotos = store.get(selectedPhotosAtom)

	if (adjustments) {
		for (const photo of selectedPhotos) {
			// TODO: if photo is already loaded into the main editor, just skip queue and apply directly
			queuePasteAdjustments(photo, adjustments)
		}
		showToast(i18n.t("store:storeHistory.editsPasted"))
	}
}

export function updateWhiteBalanceSettingIfNeeded(adjustments: Adjustments) {
	const editor = getEditor()

	const currentTemperature =
		adjustments.temperature ?? Math.round(editor.whiteBalance.asShotTemperature)
	const currentTint = adjustments.tint ?? Math.round(editor.whiteBalance.asShotTint)

	if (
		currentTemperature === Math.round(editor.whiteBalance.asShotTemperature) &&
		currentTint === Math.round(editor.whiteBalance.asShotTint)
	) {
		store.set(currentWhiteBalanceSettingAtom, "as-shot")
	} else {
		store.set(currentWhiteBalanceSettingAtom, "custom")
	}
}
